import React from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import FacebookLogin from 'react-facebook-login';
import api from '@/api';
// images
import FacebookSignInIcon from '@/public/images/facebook-login-icon.svg';
import { FacebookButtonIcon } from '@/components/landing-page-templates/ConfigLandingPage/LandingPageIcons';
import {
  FacebookIcon,
  FacebookFillIcon,
} from '@/components/landing-page-templates/ConfigLandingPage/LandingPageIcons';
// styles
import styles from './styles.module.scss';
// types
import { ILoginType } from '@/components/landing-page-templates/ConfigLandingPage/types';
import { IAccountRegistrationEnum } from '@/models/account/types';
// configurations
import { FACEBOOK_APPLICATION_ID } from '@/config';

const FacebookLoginButton = ({
  updateLoginMethod,
  updateLastLoginMethod,
  existingLoginMethod,
  signInButton = false,
  isAttendeeRegistration,
  setCheckRegistrationExists,
  isSponsorLandingPage = false,
  organizationId,
}) => {
  const dispatch = useDispatch();
  const { eventId } = useParams<{ eventId: string }>();

  // Handle errors with the Facebook Login Button
  const responseFacebookError = response => {
    console.error(response);
  };

  // Handle a successful Google Login Button response
  const responseFacebook = authJson =>
    api.account
      .facebookLogin_landingPage(
        authJson,
        eventId,
        organizationId,
        isAttendeeRegistration,
      )
      .then(({ data }) => {
        dispatch({ type: 'account/setAccount' });
        dispatch({ type: 'account/setUserData', payload: { data } });
        dispatch({
          type: 'account/setRegistrationStep',
          payload: { registrationStep: IAccountRegistrationEnum.COMPLETE },
        });
      })
      .catch(e => {
        console.error(e);
        // NOTE - checking for the 500 error with the custom message
        //  "INVITE_ONLY_NO_ACCESS" for this case. Was trying to return 403
        //  "Forbidden" but it was not working on the production environment.
        if (
          e.response?.status === 500 &&
          e.response?.data?.message === 'INVITE_ONLY_NO_ACCESS'
        ) {
          dispatch({
            type: 'account/setRegistrationStep',
            payload: { registrationStep: IAccountRegistrationEnum.NO_ACCESS },
          });
        } else if (
          e.response?.status === 401 &&
          e.response?.data?.message === 'INVITE_ONLY_NO_ACCESS'
        ) {
          dispatch({
            type: 'account/setRegistrationStep',
            payload: { registrationStep: IAccountRegistrationEnum.NO_ACCESS },
          });
        } else if (
          e?.response?.status === 406 &&
          e?.response?.data.message === 'NOT_ACCEPTABLE'
        ) {
          setCheckRegistrationExists(false);
          dispatch({
            type: 'global/addDangerToast',
            payload: { description: e?.response?.data.description },
          });
        }
        // Event Level login
        // 406 "Not Acceptable" error means they are using the wrong auth mode/type
        else if (
          e.response?.status === 406 &&
          updateLoginMethod &&
          updateLastLoginMethod
        ) {
          // Set the Login method to Facebook and set the last login method from the API response (406 means the auth methods don't match)
          updateLoginMethod(ILoginType.FACEBOOK);
          updateLastLoginMethod(e.response.data);
        }
        // App level login
        else if (e.response?.status === 406) {
          dispatch({
            type: 'global/addDangerToast',
            payload: {
              description: `You need to sign in with ${e.response.data}`,
            },
          });
        } else {
          // Some other error occurred while logging in with facebook
          dispatch({
            type: 'global/addDangerToast',
            payload: { description: 'Unable to sign in with Facebook.' },
          });
        }
      });

  const faceBookImageWithText = (
    <div>
      {existingLoginMethod ? (
        <div className={styles.buttonTextContainer}>
          <FacebookButtonIcon />
          <div
            data-testid="sign-in-with-facebook"
            className={styles.facebookButtonText}
          >
            {signInButton ? `Sign in with Facebook` : `Continue with Facebook`}
          </div>
        </div>
      ) : (
        <div>
          <FacebookFillIcon color="#1877F2" />
        </div>
      )}
    </div>
  );

  return (
    <FacebookLogin
      appId={FACEBOOK_APPLICATION_ID}
      // Prevents the SDK from auto loading (should load when the user clicks the facebook button)
      autoLoad={false}
      fields="id,first_name,last_name,email,picture"
      callback={responseFacebook}
      onFailure={responseFacebookError}
      authType="reauthenticate"
      cssClass={
        existingLoginMethod
          ? styles.facebookPopupButton
          : styles.socialLoginPopupIcon
      }
      textButton=""
      icon={faceBookImageWithText}
    />
  );
};

export default FacebookLoginButton;
